/**
 * Integrations domain API — ngx-Integration (Spec 037), Windows-Drive-Integration (Spec 041)
 * (Spec 059, E2). Moved bitgleich from the former monolithic `api.ts` — no behavior change (E5).
 */
import type { ApiCore } from './core';

/** Response from GET/PATCH api/v1/ngx-integration/status (Spec 037 S4, S9). */
export interface NgxIntegrationStatusResponse {
  enabled: boolean;
  baseUrl: string;
  username: string;
  password: string;
}

/** Response from GET/PATCH api/v1/filesync-integration/status (Spec 041 S4, S9). */
export interface FileSyncIntegrationStatusResponse {
  enabled: boolean;
  syncUrl: string;
  username: string;
  password: string;
  clientDownloadUrl: string;
}

export class IntegrationsApi {
  constructor(private readonly core: ApiCore) {}

  /**
   * Reads the current ngx-Integration activation status + display-only credentials
   * (authenticated, any registered user — Beta-Scope). GET /api/v1/ngx-integration/status.
   * Spec 037 S4, S8.
   */
  async getNgxIntegrationStatus(): Promise<NgxIntegrationStatusResponse> {
    return this.core.fetchJson<NgxIntegrationStatusResponse>(
      `${this.core.baseUrl}/api/v1/ngx-integration/status`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /**
   * Toggles the ngx-Integration activation flag as the current user (authenticated).
   * PATCH /api/v1/ngx-integration/status → 200 with the reflected new state.
   * Spec 037 S9.
   */
  async updateNgxIntegrationStatus(enabled: boolean): Promise<NgxIntegrationStatusResponse> {
    return this.core.fetchJson<NgxIntegrationStatusResponse>(
      `${this.core.baseUrl}/api/v1/ngx-integration/status`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(this.core.getAuthHeaders() as Record<string, string>),
        },
        body: JSON.stringify({ enabled }),
      },
    );
  }

  /**
   * Reads the current Windows-Drive-Integration activation status + display-only credentials +
   * client download URL (authenticated, any registered user — Beta-Scope).
   * GET /api/v1/filesync-integration/status. Spec 041 S4, S9.
   */
  async getFileSyncIntegrationStatus(): Promise<FileSyncIntegrationStatusResponse> {
    return this.core.fetchJson<FileSyncIntegrationStatusResponse>(
      `${this.core.baseUrl}/api/v1/filesync-integration/status`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /**
   * Toggles the Windows-Drive-Integration activation flag as the current user (authenticated).
   * PATCH /api/v1/filesync-integration/status → 200 with the reflected new state.
   * Spec 041 S10.
   */
  async updateFileSyncIntegrationStatus(
    enabled: boolean,
  ): Promise<FileSyncIntegrationStatusResponse> {
    return this.core.fetchJson<FileSyncIntegrationStatusResponse>(
      `${this.core.baseUrl}/api/v1/filesync-integration/status`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(this.core.getAuthHeaders() as Record<string, string>),
        },
        body: JSON.stringify({ enabled }),
      },
    );
  }
}
